import { useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import { z } from "zod";
import { BASIC_WHITE, BASIC_YELLOW } from "../../../constants/colors";
import { useAsset } from "../../../hooks/useAsset";
import { Button } from "../../atoms/Button";
import { Input } from "../../atoms/Input";
import FormTemplate from "../../templates/FormTemplate";

type Props = {
  handleSubmit: (code: string) => void;
};

export const CustomerInfoAuthForm: React.FC<Props> = ({ handleSubmit }) => {
  const { showSms } = useAsset();
  const schema = useMemo(() => {
    return z.object({
      code: z.string().min(1),
    });
  }, []);

  const [methods, setMethods] = useState<UseFormReturn<
    FieldValues,
    any,
    undefined
  > | null>(null);

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods}
      defaultValues={{ code: "" }}
      onSubmit={({ code }) => handleSubmit(code)}
    >
      <InputContainer>
        <Input
          name="code"
          type="text"
          label="認証コード"
          placeholder="5桁の認証コード"
        />
      </InputContainer>

      {showSms ? (
        <>
          <ContentWrapper>
            <TitleContent>※認証ができない場合</TitleContent>
            <BodyText>
              <ul>
                <li>
                  ・認証コードの有効期限が切れた場合は、新規登録画面から認証コードを再発行してください。
                </li>
                <li>
                  ・メールまたは、携帯番号のSMSに送られた認証コードを確認し、再度入力してください。
                </li>
              </ul>
            </BodyText>
          </ContentWrapper>
          <ContentWrapper>
            <TitleContent2>
              メールアドレスを送信されたお客様で認証コードが届かない場合は、以下の手順で携帯電話番号の送信をお試しください。
            </TitleContent2>
            <Content>
              <StepNum>1</StepNum>
              新規会員登録画面で、携帯電話番号を入力し「送信する」を押してください。
            </Content>
            <Content>
              <StepNum>2</StepNum>
              携帯電話のSMSに認証コードが届きます。
            </Content>
            <Content>
              <StepNum>3</StepNum>
              届いた「認証コード」を認証コード入力画面で入力し、次へ進んでください。
            </Content>
          </ContentWrapper>
          <ContentWrapper>
            <TitleContent2>
              SMSが届かない場合は、
              <br />
              下記の注意事項をご確認してください。また、サーバが混雑している場合もございますので、しばらく待ってからお試しください。
            </TitleContent2>
            <BodyText>
              <ul>
                <li>
                  ・モバイル通信ができない状態になっていないかご確認ください。
                </li>
                <li>・SMSの受信拒否設定をされていないかをご確認ください。</li>
                <li>
                  ・SMSをご利用できない電話・PHS・携帯電話ではSMS認証がご利用いただけません。
                </li>
              </ul>
            </BodyText>
          </ContentWrapper>
        </>
      ) : (
        <ContentWrapper>
          <TitleContent>※認証ができない場合</TitleContent>
          <BodyText>
            <ul>
              <li>
                ・認証コードの有効期限が切れた場合は、新規登録画面から認証コードを再発行してください。
              </li>
              <li>
                ・メールに送られた認証コードを確認し、再度入力してください。
              </li>
            </ul>
          </BodyText>
        </ContentWrapper>
      )}

      <ButtonWrapper>
        <Button btnStyle="primary" text="次へ" type="submit" rounded="md" />
      </ButtonWrapper>
    </FormTemplate>
  );
};

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 0;
  button {
    height: auto;
    line-height: 1;
    font-weight: 600;
    padding: 20px 0 18px;
  }
`;

const TitleContent = styled.p`
  text-align: left;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  margin-bottom: 8px;
  font-family: "Hiragino Sans";
`;

const InputContainer = styled.div`
  margin-bottom: 32px;
`;

const TitleContent2 = styled.p`
  text-align: left;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  margin-bottom: 32px;
  font-family: "Hiragino Sans";
`;

const ContentWrapper = styled.div`
  padding: 0px 5px 32px;
`;

const Content = styled.p`
  font-weight: 300;
  text-align: left;
  font-size: 16px;
  margin: 0 0 16px;
  display: flex;
  align-items: flex-start;
  font-family: "Hiragino Sans";
`;

const BodyText = styled.div`
  ul {
    font-size: 16px;
    line-height: 24px;
    font-weight: 300;
    margin: 0 0 0 1em;
    padding: 0;
    li {
      list-style: none;
      text-indent: -1em;
      margin: 0 0 16px 0;
      font-family: "Hiragino Sans";
    }
  }
`;

const StepNum = styled.span`
  color: ${BASIC_WHITE};
  font-size: 16px;
  font-weight: 600;
  line-height: 1em;
  padding: 6px 8px 4px 8px;
  border-radius: 4px;
  margin-right: 15px;
  background-color: ${BASIC_YELLOW};
  vertical-align: text-top;
  white-space: nowrap;
  flex-shrink: 0;
`;
